import { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { GlobalContext } from '../../ui/context/global/GlobalContext';
import { TYPES } from '../../config/constants/typesReducers';
import AuthService from '../../ui/services/auth-service';
import logo from '../../assets/webp/logo_complete.webp';
import logoWhite from '../../assets/webp/logo_complete_white.webp';
import { useThemeMode } from '../../ui/context/theme/ThemeProvider';

export const LoginPage = () => {
    const navigate = useNavigate();
    const { userDispatch } = useContext(GlobalContext);
    const { mode } = useThemeMode();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendMsg, setResendMsg] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error del campo al escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validar email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Email inválido';
        }

        // Validar password
        if (!formData.password) {
            newErrors.password = 'La contraseña es requerida';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const result = await AuthService.login(formData);

            if (result.success) {
                // Dispatch LOGIN action con los datos del usuario
                userDispatch({
                    type: TYPES.AUTHENTICATION.LOGIN,
                    payload: result.user
                });

                // Redirigir a home
                navigate('/');
            } else {
                if (result.emailNotVerified) {
                    setEmailNotVerified(true);
                    setErrors({ submit: result.error || 'Verifica tu email antes de iniciar sesión.' });
                } else {
                    setErrors({ submit: result.error });
                }
            }
        } catch (error) {
            setErrors({ submit: 'Error inesperado. Por favor intenta de nuevo.' });
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!formData.email) {
            setResendMsg({ type: 'error', text: 'Ingresa tu email para reenviar el correo de verificación.' });
            return;
        }
        setResendLoading(true);
        setResendMsg(null);
        try {
            const result = await AuthService.resendConfirmationEmail(formData.email);
            if (result.success) {
                setResendMsg({ type: 'success', text: 'Correo de verificación enviado. Revisa tu bandeja de entrada (y spam).' });
            } else {
                setResendMsg({ type: 'error', text: result.error || 'No se pudo reenviar el correo.' });
            }
        } catch {
            setResendMsg({ type: 'error', text: 'Error inesperado al reenviar.' });
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Container component="main" maxWidth="xs">
                <Paper
                    elevation={6}
                    sx={{
                        p: { xs: 5, sm: 6 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '420px',
                        mx: 'auto',
                        borderRadius: 2
                    }}
                >
                    <Box
                        component="img"
                        src={mode === 'dark' ? logoWhite : logo}
                        alt="Neftis SecureSign"
                        sx={{ 
                            width: '70%',
                            maxWidth: '280px',
                            mb: 4,
                            mt: 0
                        }}
                    />
                    <Typography component="h1" variant="h5" fontWeight="600" gutterBottom>
                        Iniciar Sesión
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Accede a tu cuenta de SecureSign
                    </Typography>

                    {errors.submit && (
                        <Alert severity="error" sx={{ width: '100%', mt: 1, mb: emailNotVerified ? 1 : 2 }}>
                            {errors.submit}
                        </Alert>
                    )}

                    {emailNotVerified && (
                        <Box sx={{ width: '100%', mb: 2 }}>
                            {resendMsg && (
                                <Alert severity={resendMsg.type} sx={{ mb: 1 }} onClose={() => setResendMsg(null)}>
                                    {resendMsg.text}
                                </Alert>
                            )}
                            <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                disabled={resendLoading}
                                onClick={handleResend}
                                startIcon={resendLoading ? <CircularProgress size={14} /> : null}
                            >
                                {resendLoading ? 'Enviando...' : 'Reenviar correo de verificación'}
                            </Button>
                        </Box>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Correo Electrónico"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Contraseña"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={formData.password}
                            onChange={handleChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ mt: 2, mb: 2, py: 1.2 }}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                    Iniciando sesión...
                                </>
                            ) : (
                                'Iniciar Sesión'
                            )}
                        </Button>

                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                ¿No tienes una cuenta?{' '}
                                <Link
                                    to="/register"
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        fontWeight: '600'
                                    }}
                                >
                                    Regístrate aquí
                                </Link>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <Link
                                    to="/forgot-password"
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none'
                                    }}
                                >
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;

